import {
    afterRenderEffect,
    Component,
    computed,
    inject,
    Injector,
    input,
    OnInit,
    output,
    signal,
    untracked,
    viewChild,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    IEmitCreateMessageClinicMessageSocket,
    IEmitGetAllMessageClinicMessageSocket,
    IEmitJoinRoomClinicMessageSocket,
    IJoinRoomMessageSocket,
    IMediaMessage,
    IMessage,
} from 'app/core/models/message/message';
import { BaseComponent } from 'app/layout/common/base/base.component';
import { first, Observable, of, Subscription } from 'rxjs';
import { ClinicMessageSocketService } from 'app/core/services/message/clinic-message-socket.service';
import { IModalConfig, KAudioPlayerComponent, ModalService, ToastrService } from '@kiwid-app/kiwid-ui-lib-angular';
import { ErrorV2Helper } from 'app/core/helpers/error-v2.helper';
import { DEFAULT_PAGINATION } from 'app/core/const/pagination.const';
import { IParamPagination } from 'app/core/models/api/pagination';
import { IScrollStateScrollInvertedDirective, ScrollInvertedDirective } from 'app/core/directive/scroll-inverted.directive';
import { calculateProportionalDimensions, isLastPage } from 'app/core/helpers/util';
import { AuthenticationService } from 'app/core/services/authentication/authentication.service';
import { parseDateWithTime } from 'app/core/helpers/date.helper';
import { KDateFormat, KDateIs, KDateNow } from '@kiwid-app/kiwid-date-typescript';
import { TimezoneService } from 'app/core/services/timezone/timezone.service';
import { EMediaType } from 'app/core/enums/media-type.enum';
import { DATE_API } from 'app/core/const/date.const';
import { locale as portuguese } from 'app/modules/production/order/detail/clinic-message/list-clinic-message/i18n/pt';
import { locale as english } from 'app/modules/production/order/detail/clinic-message/list-clinic-message/i18n/en';
import { locale as spanish } from 'app/modules/production/order/detail/clinic-message/list-clinic-message/i18n/es';
import { AvatarComponent } from 'ngx-avatars';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SafeUrl } from '@angular/platform-browser';
import { HashHelper } from 'app/core/helpers/hash.helper';
import { ModalImageComponent } from 'app/modules/production/order/detail/chat/modal-image/modal-image.component';
import { ComponentPortal } from '@angular/cdk/portal';
import { IMedia } from 'app/core/models/media/media';
import { ModalPreviewFileComponent } from 'app/modules/production/order/detail/chat/modal-preview-file/modal-preview-file.component';
import { FileHelper } from 'app/core/helpers/file.helper';
import { IMediaPreview } from 'app/modules/production/order/detail/chat/modal-preview/modal-preview.component';
import { MediaService } from 'app/core/services/media/media.service';
import { MatIcon } from '@angular/material/icon';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';

export const TEMP_PROVIDER_ID = 'APP_MESSAGE_TEMP_PROVIDER_ID';

export type IMessageMessageComponent = Omit<IMessage, 'media'> & {
    media?: Omit<IMediaMessage, 'url'> & {
        url: string | SafeUrl;
    };
};

@Component({
    selector: 'app-list-clinic-message',
    imports: [ScrollInvertedDirective, AvatarComponent, MatProgressSpinner, MatIcon, NgxSkeletonLoaderComponent, KAudioPlayerComponent],
    templateUrl: './list-clinic-message.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './list-clinic-message.component.scss',
})
export class ListClinicMessageComponent extends BaseComponent implements OnInit {
    private readonly authenticationService = inject(AuthenticationService);
    private readonly clinicMessageSocketService = inject(ClinicMessageSocketService);
    private readonly injector = inject(Injector);
    private readonly mediaService = inject(MediaService);
    private readonly modalService = inject(ModalService);
    private readonly timezoneService = inject(TimezoneService);
    private readonly toastrService = inject(ToastrService);

    public readonly orderDataSelected = input.required<IEmitJoinRoomClinicMessageSocket | null>();
    public readonly emitFirstPage = output<void>();

    private timeoutGoToBottom: NodeJS.Timeout | null = null;
    private readonly refScrollInverted = viewChild(ScrollInvertedDirective);
    private readonly roomDataSelected = signal<IJoinRoomMessageSocket | null>(null);
    protected readonly isLoading = signal(true);
    protected readonly isLoadingPage = signal(true);
    private readonly isLastPage = signal(false);
    private readonly parameterPagination = signal<IParamPagination>({
        offset: 0,
        limit: DEFAULT_PAGINATION,
        search: '',
    });
    private readonly messages = signal<IMessageMessageComponent[]>([]);
    protected readonly isScrollBottom = signal(true);
    protected readonly scrollState = signal<IScrollStateScrollInvertedDirective>({
        scrollTop: 0,
        scrollHeight: 0,
        clientHeight: 0,
    });
    protected readonly viewScrollBottom = signal<{ enabled: boolean; message?: number }>({
        enabled: false,
        message: null,
    });
    private readonly videoDurations = signal<{ key: number; duration: number }[]>([]);
    protected readonly viewMessage = computed(() => {
        const messages = this.messages();
        const orderDataSelected = this.orderDataSelected();
        const userId = this.authenticationService.user()?.id;

        if (!orderDataSelected?.locationId || messages.length === 0) {
            return [];
        }

        const messagesSorted = messages.sort((a, b) => (KDateIs(parseDateWithTime(a.createdAt), 'before', parseDateWithTime(b.createdAt)) ? -1 : 1));

        return messagesSorted.flatMap((message, iMessage) => {
            let text = message.message ?? '';

            const prevDate = iMessage > 0 ? KDateFormat(parseDateWithTime(messagesSorted[iMessage - 1].createdAt), DATE_API) : '';
            const currentDate = KDateFormat(parseDateWithTime(message.createdAt), DATE_API);

            const isDate = prevDate !== currentDate;

            let mediaType = 'raw';
            if (message.media?.type === EMediaType.VIDEO) {
                mediaType = 'video';
            } else if (message.media?.type === EMediaType.AUDIO) {
                mediaType = 'audio';
            } else if (message.media?.type === EMediaType.IMAGE) {
                mediaType = 'image';
            }

            const isLast =
                iMessage === messagesSorted.length - 1 ||
                messagesSorted[iMessage + 1]?.userId !== message.userId ||
                KDateFormat(parseDateWithTime(messagesSorted[iMessage + 1]?.createdAt || message.createdAt), DATE_API) !== currentDate;
            const key = message.id;

            const dimention = message.media?.parameters?.dimensions?.width
                ? calculateProportionalDimensions({
                      width: message.media.parameters.dimensions.width,
                      height: message.media.parameters.dimensions.height,
                      maxHeight: mediaType === 'image' ? 200 : 220,
                      minHeight: 100,
                  })
                : null;
            const waves = message.media?.parameters?.waves ?? [];

            const isDeleted = !!message.deletedAt;

            if (isDeleted) {
                text = this.translateKey('DELETED_MESSAGE');
            }

            const currentMessage = {
                key,
                text,
                hour: KDateFormat(parseDateWithTime(message.createdAt), 'HH:mm'),
                media: message.media
                    ? {
                          type: mediaType,
                          isLoading: message.media.providerId === TEMP_PROVIDER_ID,
                          url: message.media.url,
                          title: message.media.originalName,
                          waves,
                          style: !!dimention
                              ? `width: ${dimention.width}px !important; height: ${dimention.height}px !important;`
                              : 'min-height: 100px; max-height: 150px',
                          duration: computed(() => {
                              const seconds = this.videoDurations().find((videoDuration) => videoDuration.key === key)?.duration ?? -1;
                              const minutes = Math.floor(seconds / 60);
                              const remainingSeconds = Math.floor(seconds % 60);
                              return seconds < 0 || isNaN(seconds) || !isFinite(seconds)
                                  ? null
                                  : `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
                          }),
                          onLoadVideo: (video: HTMLVideoElement) => {
                              this.videoDurations.update((prev) => {
                                  const videoDurations = prev.filter((videoDuration) => videoDuration.key !== key);

                                  return [
                                      ...videoDurations,
                                      {
                                          key,
                                          duration: video.duration,
                                      },
                                  ];
                              });

                              if (this.parameterPagination().offset === 0) {
                                  if (!!this.timeoutGoToBottom) {
                                      clearTimeout(this.timeoutGoToBottom);
                                  }

                                  this.timeoutGoToBottom = setTimeout(() => {
                                      this.goScrollToBottom();
                                  }, 300);
                              }
                          },
                          onLoad: () => {
                              if (this.parameterPagination().offset === 0) {
                                  if (!!this.timeoutGoToBottom) {
                                      clearTimeout(this.timeoutGoToBottom);
                                  }

                                  this.timeoutGoToBottom = setTimeout(() => {
                                      this.goScrollToBottom();
                                  }, 300);
                              }
                          },
                          onClick: () => {
                              if (mediaType == 'image') {
                                  this.openImage(message.media.url);
                              } else if (mediaType === 'raw') {
                                  this.openFileMedia(message.media as IMedia);
                              } else if (mediaType === 'video') {
                                  this.openFileMedia(message.media as IMedia);
                              }
                          },
                      }
                    : undefined,
                myMessage: userId == message.userId,
                isDeleted,
                isDate: false,
                isLast,
                user: {
                    enabled: userId != message.userId && isLast,
                    name: message.userName,
                    color: message.color ?? '#235881',
                    avatarUrl: message.userAvatarUrl,
                },
            };

            const messages = [currentMessage];

            if (isDate) {
                messages.splice(0, 0, {
                    key: message.id * -1 - 100000,
                    text: this.formatDate(parseDateWithTime(message.createdAt), this.completeDateFormat),
                    hour: KDateFormat(parseDateWithTime(message.createdAt), 'HH:mm'),
                    myMessage: true,
                    media: undefined,
                    isDeleted: false,
                    isDate,
                    isLast: false,
                    user: {
                        enabled: false,
                        name: '',
                        color: '',
                        avatarUrl: null,
                    },
                });
            }

            return messages;
        });
    });
    protected readonly isEmpty = computed(() => !this.orderDataSelected() || (this.viewMessage().length === 0 && !this.isLoadingPage()));

    ngOnInit(): void {
        this.registerOnTranslate('LIST_CLINIC_MESSAGE', portuguese, english, spanish);
        this.registerScrollBottom();
        this.getData();
    }

    private get completeDateFormat(): string {
        return this.translateKey('COMPLETE_DATE_FORMAT');
    }

    public goScrollToBottom(): void {
        this.isScrollBottom.set(true);
        this.refScrollInverted()?.scrollToBottom('instant');
    }

    public createMessage(data: Pick<IEmitCreateMessageClinicMessageSocket, 'message'>): Observable<boolean> {
        const roomDataSelected = this.roomDataSelected();
        const orderDataSelected = this.orderDataSelected();

        if (!roomDataSelected || !orderDataSelected) {
            return of(false);
        }

        const user = this.authenticationService.user();
        const dateNow = KDateNow().toISOString();
        const referenceId = HashHelper.uuidv4();

        const message: IMessage = {
            id: -1 * Math.floor(Math.random() * 1000),
            userId: user.id,
            userName: user.kFullName(),
            userAvatarUrl: user.avatar?.url,
            createdAt: dateNow,
            anonymous: false,
            referenceId,
            message: data.message,
        };

        this.messages.update((prev) => [...prev, message]);

        const parameter: IEmitCreateMessageClinicMessageSocket = {
            laboratoryId: orderDataSelected.laboratoryId,
            locationId: orderDataSelected.locationId,
            orderId: orderDataSelected.orderId,
            roomId: roomDataSelected.roomId,
            message: data.message,
            referenceId,
        };

        return this.emitMessage(parameter);
    }

    public createMessageFile(previewMedia: IMediaPreview[]): Observable<boolean> {
        if (previewMedia.length === 0) {
            return of(false);
        }

        const orderDataSelected = this.orderDataSelected();
        const roomDataSelected = this.roomDataSelected();
        const user = this.authenticationService.user();
        const dateNow = KDateNow().toISOString();

        const messages: IMessageMessageComponent[] = previewMedia.map((media) => {
            const id = -1 * Math.floor(Math.random() * 1000);
            const referenceId = HashHelper.uuidv4();

            return {
                id,
                roomId: roomDataSelected.roomId,
                userId: user.id,
                userName: user.kFullName(),
                userAvatarUrl: user.avatar?.url,
                createdAt: dateNow,
                updatedAt: dateNow,
                anonymous: false,
                referenceId,
                media: {
                    id: referenceId,
                    type: media.type,
                    originalName: media.file.name,
                    provider: media.provider,
                    url: media.url,
                    providerId: TEMP_PROVIDER_ID,
                },
            };
        });

        this.messages.update((prev) => [...prev, ...messages]);

        messages.forEach((message, iMessage) => {
            this.subscriptions.add(
                this.mediaService
                    .uploadMedia({
                        laboratoryId: orderDataSelected.laboratoryId,
                        file: previewMedia[iMessage].file,
                    })
                    .pipe(first())
                    .subscribe({
                        next: ({ data }) => {
                            const parameter: IEmitCreateMessageClinicMessageSocket = {
                                laboratoryId: orderDataSelected.laboratoryId,
                                locationId: orderDataSelected.locationId,
                                orderId: orderDataSelected.orderId,
                                roomId: roomDataSelected.roomId,
                                media: {
                                    type: data.type,
                                    originalName: data.originalName,
                                    url: data.url,
                                    providerId: data.providerId,
                                    thumbnail: data.thumbnail,
                                    provider: data.provider,
                                },
                                referenceId: message.referenceId,
                            };

                            this.subscriptions.add(this.emitMessage(parameter).pipe(first()).subscribe());
                        },
                        error: (error) => {
                            ErrorV2Helper.setToastrError(this.toastrService, error);
                        },
                    }),
            );
        });
    }

    protected onScrollStateChange(state: IScrollStateScrollInvertedDirective): void {
        this.scrollState.set(state);

        const directive = this.refScrollInverted();
        if (directive) {
            this.isScrollBottom.set(directive.isNearBottom(30));
        }
    }

    protected onScrollToTop(): void {
        if (!this.isLoadingPage() && !this.isLoading() && !this.isLastPage()) {
            this.isLoadingPage.set(true);

            this.parameterPagination.update((prev) => ({
                ...prev,
                offset: this.messages().length,
            }));
        }

        if (this.isLastPage()) {
            this.refScrollInverted()?.resetLoadingState();
        }
    }

    private openFileMedia(media: IMedia): void {
        if (media.url.indexOf('.html') > -1 || media.url.indexOf('.stl') > -1 || media.type === EMediaType.VIDEO) {
            const config: IModalConfig = {
                title: this.translateKey('MODAL.PREVIEW.TITLE'),
                type: 'full-screen',
                backgroundColor: 'var(--kiwid-surface)',
                component: new ComponentPortal<ModalPreviewFileComponent>(ModalPreviewFileComponent),
            };

            const dialog = this.modalService.openModal(config);

            this.subscriptions.add(
                dialog.componentInstance.instanceObservable<ModalPreviewFileComponent>().subscribe((data) => {
                    data.dialog = signal(dialog);
                    data.media = signal(media);
                }),
            );

            this.subscriptions.add(
                dialog
                    .afterClosed()
                    .pipe(first())
                    .subscribe(() => {
                        this.setTitle();
                    }),
            );
            return;
        }

        FileHelper.forceDownloadWithUrl(media.url, '_blank');
    }

    private openImage(imageUrl: string | SafeUrl): void {
        const config: IModalConfig = {
            title: this.translateKey('MODAL.IMAGE.TITLE'),
            type: 'full-screen',
            backgroundColor: 'var(--kiwid-bg-page)',
            footerColor: 'var(--kiwid-surface)',
            component: new ComponentPortal<ModalImageComponent>(ModalImageComponent),
        };

        const dialog = this.modalService.openModal(config);

        this.subscriptions.add(
            dialog.componentInstance.instanceObservable<ModalImageComponent>().subscribe((data) => {
                data.dialog = signal(dialog);
                data.imageUrl = signal(imageUrl);
            }),
        );

        this.subscriptions.add(
            dialog
                .afterOpened()
                .pipe(first())
                .subscribe(() => {
                    this.subscriptions.add(
                        dialog.componentInstance.buttonEventClick.subscribe(() => {
                            dialog.close();
                        }),
                    );
                }),
        );
    }

    private emitMessage(data: IEmitCreateMessageClinicMessageSocket): Observable<boolean> {
        return new Observable<boolean>((observer) => {
            this.subscriptions.add(
                this.clinicMessageSocketService
                    .emitCreateMessage(data)
                    .pipe(first())
                    .subscribe({
                        next: () => {
                            this.goScrollToBottom();
                            observer.next(true);
                        },
                        error: (error) => {
                            observer.next(false);
                            ErrorV2Helper.setToastrError(this.toastrService, error);
                        },
                        complete: () => {
                            observer.complete();
                        },
                    }),
            );
        });
    }

    private getData(): void {
        const joinSocket = () => {
            const subscriptions: Subscription[] = [];

            const runOnSubscribe = (): void => subscriptions.forEach((subscription) => subscription.unsubscribe());

            const getData = (parameter: IEmitJoinRoomClinicMessageSocket): void => {
                runOnSubscribe();

                let roomId: string | undefined = undefined;

                this.isLoading.set(true);

                subscriptions.push(
                    this.clinicMessageSocketService
                        .emitJoinRoom(parameter)
                        .pipe(first())
                        .subscribe({
                            next: (data) => {
                                this.roomDataSelected.set(data);
                                roomId = data.roomId;
                                this.isLoading.set(false);
                            },
                            error: (error) => {
                                ErrorV2Helper.setToastrError(this.toastrService, error);
                                this.isLoading.set(false);
                            },
                        }),
                );

                const subscriptionDestroy = new Subscription();
                subscriptionDestroy.add({
                    unsubscribe: () => {
                        if (!!roomId) {
                            this.clinicMessageSocketService.emitLeaveRoom({
                                roomId: roomId!,
                                locationId: parameter.locationId,
                            });
                        }
                    },
                });
                subscriptions.push(subscriptionDestroy);
            };

            this.subscriptions.add({
                unsubscribe: () => {
                    runOnSubscribe();
                },
            });

            afterRenderEffect(
                () => {
                    const selectedData = this.orderDataSelected();
                    const isConnected = this.clinicMessageSocketService.isConnected();

                    untracked(() => {
                        runOnSubscribe();

                        if (!!selectedData && isConnected) {
                            this.roomDataSelected.set(null);
                            getData(selectedData);
                        } else {
                            this.roomDataSelected.set(null);
                        }
                    });
                },
                {
                    injector: this.injector,
                },
            );
        };
        joinSocket();

        const messageSocket = (): void => {
            const subscriptions: Subscription[] = [];

            const runOnSubscribe = (): void => subscriptions.forEach((subscription) => subscription.unsubscribe());

            const getData = (parameter: IEmitGetAllMessageClinicMessageSocket): void => {
                runOnSubscribe();

                subscriptions.push(
                    this.clinicMessageSocketService.messages.subscribe((messages) => {
                        this.viewScrollBottom.update((prev) => ({
                            ...prev,
                            message: prev.enabled ? (prev.message ?? 0) + messages.length : null,
                        }));

                        this.messages.update((prev) => {
                            const allMessages = [...prev];

                            messages.forEach((message) => {
                                const iMessage = allMessages.findIndex(
                                    (messageFind) =>
                                        messageFind.id === message.id ||
                                        (!!messageFind.referenceId && messageFind.referenceId === message.referenceId),
                                );

                                if (iMessage > -1) {
                                    allMessages.splice(iMessage, 1, message);
                                } else {
                                    allMessages.push(message);
                                }
                            });

                            return allMessages;
                        });
                    }),
                );

                if (parameter.offset === 0) {
                    this.isLoading.set(true);
                    this.messages.set([]);
                    this.refScrollInverted()?.resetLoadingState();
                    this.goScrollToBottom();
                }

                this.isLoadingPage.set(true);

                subscriptions.push(
                    this.clinicMessageSocketService
                        .emitGetAllMessage(parameter)
                        .pipe(first())
                        .subscribe({
                            next: ({ data, meta }) => {
                                if (meta.pagination.offset === 0) {
                                    this.messages.set([]);
                                }
                                this.messages.update((prev) => [...prev, ...data]);
                                this.isLastPage.set(isLastPage(meta));
                                this.isLoadingPage.set(false);
                                this.isLoading.set(false);
                                if (meta.pagination.offset === 0) {
                                    this.emitFirstPage.emit();
                                    this.goScrollToBottom();
                                }
                            },
                            error: (error) => {
                                ErrorV2Helper.setToastrError(this.toastrService, error);
                                this.isLastPage.set(true);
                                this.isLoadingPage.set(false);
                                this.isLoading.set(false);
                            },
                        }),
                );
            };

            this.subscriptions.add({
                unsubscribe: () => runOnSubscribe(),
            });

            let prevOrderId: string | undefined = undefined;
            afterRenderEffect(
                () => {
                    const roomDataSelected = this.roomDataSelected();
                    const selectedData = this.orderDataSelected();
                    const parameterPagination = this.parameterPagination();
                    const isConnected = this.clinicMessageSocketService.isConnected();

                    untracked(() => {
                        runOnSubscribe();

                        if (prevOrderId !== selectedData?.orderId) {
                            prevOrderId = selectedData?.orderId;
                            this.parameterPagination.update((prev) => ({ ...prev, offset: 0 }));
                            return;
                        }

                        if (!!roomDataSelected && !!selectedData && isConnected) {
                            getData({
                                ...selectedData,
                                ...roomDataSelected,
                                ...parameterPagination,
                            });
                        } else {
                            if (parameterPagination.offset !== 0) {
                                this.parameterPagination.update((prev) => ({ ...prev, offset: 0 }));
                            }
                            runOnSubscribe();
                        }
                    });
                },
                {
                    injector: this.injector,
                },
            );
        };
        messageSocket();
    }

    private registerScrollBottom(): void {
        let wasLoading = false;

        afterRenderEffect(
            () => {
                const messages = this.messages();
                const isLoadingPage = this.isLoadingPage();
                const directive = this.refScrollInverted();

                untracked(() => {
                    if (wasLoading && !isLoadingPage && directive && messages.length > this.parameterPagination().limit) {
                        directive.restoreScrollPosition();
                    }

                    wasLoading = isLoadingPage;
                });
            },
            { injector: this.injector },
        );

        afterRenderEffect(
            () => {
                this.viewMessage();
                const isScrollBottom = this.isScrollBottom();
                const directive = this.refScrollInverted();
                const isLoading = this.isLoading();

                untracked(() => {
                    this.viewScrollBottom.update((prev) => ({
                        ...prev,
                        enabled: !isScrollBottom,
                        message: isScrollBottom ? null : prev.message,
                    }));

                    if (directive && isScrollBottom && !isLoading) {
                        directive.scrollToBottom('instant');
                    }
                });
            },
            { injector: this.injector },
        );
    }
}
